/**
 * Web building blocks for the subscription journeys (A-J, L1-L3). Styling
 * lives in `global.css` under the `tabbio-sub-*` prefix so every screen shares
 * one set of tokens (light/dark via `[data-theme]`, RTL via logical
 * properties). Presentation only: journey state comes from
 * `lib/subscription-journey` and its hooks.
 */
import {
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { PlanTier, RevenueCatStore } from '@tabbio/types';
import { WebDialog } from '../common/WebDialog.web';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { useLocale } from '../../hooks/useLocale';
import { SparkMark } from './SparkMark.web';
import { NovaFlatMark } from './NovaFlatMark.web';
import { privacyUrl, termsUrl } from '../../lib/subscription-journey/store-links';

type ButtonKind = 'primary' | 'secondary' | 'text';

export function SubButton({
  kind = 'primary',
  icon,
  children,
  className,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  kind?: ButtonKind;
  icon?: ReactNode;
}) {
  const kindClass =
    kind === 'secondary' ? ' is-secondary' : kind === 'text' ? ' is-text' : '';
  return (
    <button
      type={type}
      className={`tabbio-sub-btn${kindClass}${className ? ` ${className}` : ''}`}
      {...rest}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}

/** Material Symbol inside a button or row; decorative. */
export function Icon({
  name,
  size,
  flip = false,
}: {
  name: string;
  size?: number;
  /** Mirror directional glyphs (chevrons, arrows) in RTL. */
  flip?: boolean;
}) {
  return (
    <MaterialSymbol
      name={name}
      size={size}
      className={flip ? 'tabbio-sub-flip' : undefined}
    />
  );
}

export function SubHeading({
  title,
  subtitle,
  center = false,
  id,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  center?: boolean;
  id?: string;
}) {
  return (
    <div>
      <h1 id={id} className={`tabbio-sub-heading${center ? ' is-center' : ''}`}>
        {title}
      </h1>
      {subtitle ? (
        <p className={`tabbio-sub-subtitle${center ? ' is-center' : ''}`}>
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

export function SubGroup({ children }: { children: ReactNode }) {
  return <div className="tabbio-sub-group">{children}</div>;
}

export function SubRow({
  icon,
  title,
  sub,
  value,
  onClick,
  danger = false,
  chevron = true,
  disabled,
  ariaLabel,
}: {
  icon?: string;
  title: ReactNode;
  sub?: ReactNode;
  value?: ReactNode;
  onClick?: () => void;
  danger?: boolean;
  chevron?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      className={`tabbio-sub-row${danger ? ' is-danger' : ''}`}
      onClick={onClick}
      disabled={disabled || !onClick}
      aria-label={ariaLabel}
    >
      {icon ? (
        <span className="tabbio-sub-row-icon">
          <MaterialSymbol name={icon} size={22} />
        </span>
      ) : null}
      <span className="tabbio-sub-row-body">
        <span className="tabbio-sub-row-title">{title}</span>
        {sub ? <span className="tabbio-sub-row-sub">{sub}</span> : null}
      </span>
      {value ? <span className="tabbio-sub-row-value">{value}</span> : null}
      {chevron && onClick ? (
        <span className="tabbio-sub-row-chevron">
          <Icon name="chevron_right" size={20} flip />
        </span>
      ) : null}
    </button>
  );
}

export function SubInfo({
  children,
  icon = 'info',
  warning = false,
}: {
  children: ReactNode;
  icon?: string;
  warning?: boolean;
}) {
  return (
    <div className={`tabbio-sub-info${warning ? ' is-warning' : ''}`}>
      <MaterialSymbol name={icon} />
      <span>{children}</span>
    </div>
  );
}

export function SubList({
  items,
  variant = 'default',
  icon = 'check',
}: {
  items: ReactNode[];
  variant?: 'default' | 'stays' | 'stops' | 'plain';
  icon?: string;
}) {
  return (
    <ul className={`tabbio-sub-list${variant === 'default' ? '' : ` is-${variant}`}`}>
      {items.map((item, index) => (
        <li key={index}>
          <MaterialSymbol name={icon} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 className="tabbio-sub-section-title">{children}</h2>;
}

/** Plus = Spark, Pro = flat Nova, Free = none. Decorative (the name is text). */
export function TierMark({ tier, size = 20 }: { tier: PlanTier; size?: number }) {
  if (tier === 'plus') {
    return (
      <span className="tabbio-sub-mark">
        <SparkMark size={size} />
      </span>
    );
  }
  if (tier === 'pro') {
    return (
      <span className="tabbio-sub-mark">
        <NovaFlatMark size={size} color="currentColor" />
      </span>
    );
  }
  return null;
}

/** `Restore · Terms · Privacy` under purchase footers. */
export function LegalLinks({
  onRestore,
  restoreDisabled,
}: {
  onRestore?: () => void;
  restoreDisabled?: boolean;
}) {
  const { t, i18n } = useTranslation('common');
  const language = i18n.resolvedLanguage ?? i18n.language;
  return (
    <div className="tabbio-sub-legal">
      {onRestore ? (
        <button type="button" onClick={onRestore} disabled={restoreDisabled}>
          {t('subscription.legal.restore')}
        </button>
      ) : null}
      <a href={termsUrl(language)} target="_blank" rel="noopener noreferrer">
        {t('subscription.legal.terms')}
      </a>
      <a href={privacyUrl(language)} target="_blank" rel="noopener noreferrer">
        {t('subscription.legal.privacy')}
      </a>
    </div>
  );
}

/** `common` key for an EXISTING subscription's billing owner. */
export function storeProviderKey(store: RevenueCatStore | null | undefined) {
  if (store === 'app_store') return 'subscription.providers.appStore';
  if (store === 'play_store') return 'subscription.providers.googlePlay';
  return 'subscription.providers.web';
}

const dialogOpeners = new WeakMap<HTMLElement, HTMLElement>();

/**
 * Keeps Tab/Shift+Tab inside the dialog that contains `ref`, focuses the
 * dialog on open and hands focus back to the opener on close. WebDialog has a
 * scrim and Escape but no focus containment; every journey dialog adds it.
 */
export function useDialogFocus(
  ref: { current: HTMLElement | null },
  onEscape?: () => void,
) {
  const escapeRef = useRef(onEscape);
  escapeRef.current = onEscape;
  useEffect(() => {
    const dialog = ref.current?.closest<HTMLElement>('[role="dialog"]');
    if (!dialog) return;
    const previous =
      typeof document !== 'undefined'
        ? (document.activeElement as HTMLElement | null)
        : null;
    if (!dialog.contains(document.activeElement)) {
      dialog.setAttribute('tabindex', '-1');
      dialog.focus({ preventScroll: true });
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && escapeRef.current) {
        // WebDialog also listens; stop here so Escape never double-fires.
        event.stopPropagation();
        escapeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]),a[href],textarea:not([disabled]),input:not([disabled]),[tabindex="0"]',
        ),
      ).filter((node) => node.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) {
        event.preventDefault();
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    if (previous && !dialog.contains(previous) && !dialogOpeners.has(dialog)) {
      dialogOpeners.set(dialog, previous);
    }
    dialog.addEventListener('keydown', onKeyDown);
    return () => {
      dialog.removeEventListener('keydown', onKeyDown);
      // Step changes keep the dialog; only a real close hands focus back.
      setTimeout(() => {
        if (dialog.isConnected) return;
        const opener = dialogOpeners.get(dialog);
        if (opener?.isConnected) opener.focus({ preventScroll: true });
      }, 0);
    };
    // Bind once per mounted dialog element.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * The journey's dialog shell: WebDialog (centred column on desktop, bottom
 * sheet at phone widths) + focus containment + Escape = `onClose`. `onBack`
 * renders the leading `<` chevron.
 */
export function JourneyDialog({
  title,
  onClose,
  onBack,
  footer,
  children,
  maxWidth = 520,
  zIndex = 1100,
  stepKey,
}: {
  title: string;
  onClose: () => void;
  onBack?: () => void;
  footer?: ReactNode;
  children: ReactNode;
  maxWidth?: number;
  zIndex?: number;
  /** Changing it replays the step entrance and resets scroll. */
  stepKey?: string;
}) {
  const { isRTL } = useLocale();
  return (
    <WebDialog
      title={title}
      onClose={onClose}
      onBack={onBack}
      maxWidth={maxWidth}
      zIndex={zIndex}
      footer={
        footer ? (
          <div dir={isRTL ? 'rtl' : 'ltr'} className="tabbio-sub tabbio-sub-actions">
            {footer}
          </div>
        ) : undefined
      }
    >
      <DialogBody key={stepKey} onEscape={onClose}>
        {children}
      </DialogBody>
    </WebDialog>
  );
}

/**
 * A journey dialog without a title row (L1 "Happy with your CV?"): the
 * heading is the content. `label` names the dialog for assistive tech; a
 * close button sits at the trailing edge.
 */
export function BareJourneyDialog({
  label,
  onClose,
  footer,
  children,
  maxWidth = 460,
  zIndex = 1100,
  stepKey,
}: {
  label: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  maxWidth?: number;
  zIndex?: number;
  stepKey?: string;
}) {
  const { isRTL } = useLocale();
  const { t } = useTranslation('common');
  return (
    <WebDialog title={label} onClose={onClose} hideHeader maxWidth={maxWidth} zIndex={zIndex}>
      <div
        dir={isRTL ? 'rtl' : 'ltr'}
        className="tabbio-sub"
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 22px 20px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="tabbio-sub-icon-btn"
            onClick={onClose}
            aria-label={t('subscription.actions.close')}
          >
            <MaterialSymbol name="close" size={22} />
          </button>
        </div>
        <DialogBody key={stepKey} onEscape={onClose}>
          {children}
        </DialogBody>
        {footer ? (
          <div className="tabbio-sub-actions" style={{ marginTop: 18 }}>
            {footer}
          </div>
        ) : null}
      </div>
    </WebDialog>
  );
}

function DialogBody({
  children,
  onEscape,
}: {
  children: ReactNode;
  onEscape: () => void;
}) {
  const { isRTL } = useLocale();
  const ref = useRef<HTMLDivElement>(null);
  useDialogFocus(ref, onEscape);
  useEffect(() => {
    // New step: start at the top of the scroll area.
    const scroller = ref.current?.parentElement;
    if (scroller) scroller.scrollTop = 0;
  }, []);
  return (
    <div ref={ref} dir={isRTL ? 'rtl' : 'ltr'} className="tabbio-sub tabbio-sub-step-enter">
      {children}
    </div>
  );
}

/** A short informational sheet (receipts, refund, terms, provider…). */
export function InfoDialog({
  title,
  body,
  note,
  onClose,
  children,
  zIndex = 1200,
}: {
  title: string;
  body?: ReactNode;
  note?: ReactNode;
  onClose: () => void;
  children?: ReactNode;
  zIndex?: number;
}) {
  const { t } = useTranslation('common');
  return (
    <JourneyDialog
      title={title}
      onClose={onClose}
      zIndex={zIndex}
      maxWidth={460}
      footer={
        <SubButton kind="secondary" onClick={onClose}>
          {t('subscription.actions.done')}
        </SubButton>
      }
    >
      {body ? (
        <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5 }}>{body}</p>
      ) : null}
      {children}
      {note ? <SubInfo>{note}</SubInfo> : null}
    </JourneyDialog>
  );
}

/** Centered pending/error/unchanged block used by checkout and provider states. */
export function StateBlock({
  icon,
  spinner = false,
  title,
  body,
  live = 'polite',
}: {
  icon?: string;
  spinner?: boolean;
  title: ReactNode;
  body?: ReactNode;
  live?: 'polite' | 'assertive';
}) {
  return (
    <div className="tabbio-sub-state" role="status" aria-live={live}>
      {spinner ? (
        <span className="tabbio-sub-spinner" aria-hidden />
      ) : icon ? (
        <span className="tabbio-sub-provider-mark" style={{ marginBottom: 0 }}>
          <MaterialSymbol name={icon} size={30} />
        </span>
      ) : null}
      <h3>{title}</h3>
      {body ? <p>{body}</p> : null}
    </div>
  );
}
